import {createContext, useState, useEffect} from "react";
import {v4 as uuid} from "uuid";

const DataFlowContext = createContext();
function DataFlowProvider({children}) {
    const [comment, setComment] = useState({});
    const [appPort, setAppPort] = useState(null)
    const [hasAddedApiPort, setHasAddedApiPort] = useState(false)
    
    // Reset migration flag when nodeValue changes (new script loaded)
    const resetMigrationFlag = () => {
        setHasAddedApiPort(false)
    }
    const [nodeValue, setNodeValue] = useState({
        variables: [
            {
                id: uuid(),
                value: 'PROFILE_NAME',
                label: 'PROFILE_NAME',
                data: "",
                type: 'disabled',
            },
            {
                id: uuid(),
                value: 'PROFILE_ID',
                label: 'PROFILE_ID',
                data: "",
                type: 'disabled',
            },
            {
                id: uuid(),
                value: 'API_PORT',
                label: 'API_PORT',
                data: "",
                type: 'disabled',
            }
        ]
    })
    const onCreateNewVariable = (variableName) => {
        const variableExist = nodeValue.variables.find(variable => variable.value === variableName);
        if (variableExist) return;
        const newVariable = {
            id: uuid(),
            value: variableName,
            label: variableName,
            data: "",
            name: ""
        }
        setNodeValue({
            variables: [...nodeValue.variables, newVariable]
        })
    }

    const createNewVariables = (variables) => {
        const currentVariables = new Set();
        nodeValue.variables.forEach(item => currentVariables.add(item.value));
        variables.forEach(variable => {
            if (currentVariables.has(variable)) return;
            const newVariable = {
                id: uuid(),
                value: variable,
                label: variable,
                data: "",
                name: ""
            }
            setNodeValue(nodeValue => {
                nodeValue.variables.push(newVariable)
                return nodeValue
            })
        })
        return;
    }

    const updateVariableData = (variableName, newData) => {
        setNodeValue(prevNodeValue => ({
            variables: prevNodeValue.variables.map(variable => 
                variable.value === variableName 
                    ? { ...variable, data: newData }
                    : variable
            )
        }))
    }
    
    useEffect(() => {
        if (hasAddedApiPort) return;
        const exist = (nodeValue.variables || []).some(v => v.value === 'API_PORT');
        if (!exist && nodeValue.variables.length > 0) {
            setNodeValue(prev => {
                const newVariables = [...prev.variables]
                // Insert API_PORT after PROFILE_ID (position 2) if it exists
                const profileIdIndex = newVariables.findIndex(v => v.value === 'PROFILE_ID')
                const insertIndex = profileIdIndex >= 0 ? profileIdIndex + 1 : newVariables.length
                
                newVariables.splice(insertIndex, 0, {
                    id: uuid(),
                    value: 'API_PORT',
                    label: 'API_PORT',
                    data: `${appPort ?? ''}`,
                    type: 'disabled'
                })
                
                return { variables: newVariables }
            })
            setHasAddedApiPort(true)
        } else if (exist) {
            // If API_PORT already exists, mark as processed to prevent re-adding
            setHasAddedApiPort(true)
        }
    }, [nodeValue.variables.length])

    useEffect(() => {
        const APP_PORT = async () => {
            try {
                const PORT = await window.api.getApiPort()
                setAppPort(PORT || null)
                if (PORT) {
                    updateVariableData('API_PORT', `${PORT}`)
                }
            } catch (error) {
                console.error("Error getting API port:", error)
                setAppPort(null)
                updateVariableData('API_PORT', '')
            }
        }   

        APP_PORT()
    }, [])

    const value = {
        comment,
        nodeValue,
        setComment,
        onCreateNewVariable,
        createNewVariables,
        setNodeValue,
        updateVariableData,
        appPort,
        resetMigrationFlag,
    }

    return (
        <DataFlowContext.Provider value={value}>
            {children}
        </DataFlowContext.Provider>
    )
}

export {DataFlowContext, DataFlowProvider}